import useSWR from 'swr';
import { observer } from 'mobx-react-lite';
import { Hero, PageLoader } from '@cfm-media/pwa-ui-components';

import { LoadableStatus } from '../mobx-utils';
import { useStore } from '../core/store';
import { useTranslation } from '../core/utils/translations';
import { ContentWrapper } from '../components/content-wrapper';
import { ErrorPage } from '../components/error-page';
import { PageWrapper } from '../components/page-wrapper';
import { TransferCard } from '../components/transfers/card';
import { bookingTeaserConfig } from '../utils/teasers';
import { defaultFetcher } from '../utils/default-fetchers';
import { urls } from '../urls';

export const Transfer = observer(() => {
  const { t } = useTranslation('transfer-page');
  const { booking } = { ...useStore().BookingStore };

  const {
    data: transfers,
    error: transfersError,
    isLoading: transfersAreLoading,
  } = useSWR(urls.api.transfer, defaultFetcher);

  if (booking?.status !== LoadableStatus.done || transfersAreLoading) {
    return (
      <PageLoader
        variant="absolute"
        info="Transferinformationen werden geladen..."
        overlay
      />
    );
  }

  if (booking?.status === LoadableStatus.error || transfersError) {
    return <TransferErrorView />;
  }

  if (!booking?.data?.transferType) {
    return <TransferFallbackView />;
  }

  return (
    <PageWrapper>
      <Hero
        image={bookingTeaserConfig?.transfer?.image}
        iconClass="icon-bus"
        headline="Transfer"
        overlay
      />

      <ContentWrapper>
        <div className="mb-3 mt-5 text-xl text-brand-1 font-semibold uppercase">
          Ihre Transfer-Zeiten im Überblick
        </div>

        {Array.isArray(transfers) && transfers?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:w-fit">
            {transfers.map((transfer) => (
              <TransferCard
                key={transfer?.pickUp?.date}
                transfer={transfer}
                supplier={booking.data.transferType}
              />
            ))}
          </div>
        ) : (
          <p className="mt-6 text-center text-gray-500">
            {t('transfers-empty-text')}
          </p>
        )}
      </ContentWrapper>
    </PageWrapper>
  );
});

const TransferErrorView = () => {
  const { t } = useTranslation('error-pages');

  return (
    <PageWrapper>
      <div className="flex flex-col w-full h-full justify-center items-center">
        <ContentWrapper>
          <div className="flex justify-center w-full h-full">
            <img
              className="max-h-[300px]"
              src="public/assets/images/templates/Element 10.svg"
            />
          </div>
          <ErrorPage
            title={t('transfer-page-error-title-text')}
            body={t('transfer-page-error-body-text')}
            link={urls.home}
            buttonText={t('back-button-text')}
          />
        </ContentWrapper>
      </div>
    </PageWrapper>
  );
};

const TransferFallbackView = () => {
  const { t } = useTranslation('error-pages');

  return (
    <PageWrapper>
      <div className="flex flex-col w-full h-full justify-center items-center">
        <ContentWrapper>
          <div className="flex justify-center w-full h-full">
            <img
              className="max-h-[300px]"
              src="public/assets/images/templates/Element 10.svg"
            />
          </div>
          <ErrorPage
            title={
              'Ihre Abholzeit erhalten Sie 24 Stunden vor Abreise am Hoteldesk bzw. bei Ihrem Reiseleiter.'
            }
            link={urls.home}
            buttonText={t('back')}
          />
        </ContentWrapper>
      </div>
    </PageWrapper>
  );
};
